import { useMemo } from 'react';
import { useExpenses } from './useExpenses';
import { useDepartments } from './useDepartments';
import { useExpenseCategories } from './useExpenseCategories';
import { usePaymentRequests } from './usePaymentRequests';
import { format, parseISO } from 'date-fns';

export interface DepartmentCostFilters {
  dateFrom?: string;
  dateTo?: string;
}

export interface DepartmentCostKPIs {
  totalSpend: number;
  highestSpendDepartment: string;
  highestSpendAmount: number;
  avgBudgetUtilization: number;
  departmentsOverBudget: number;
  totalDepartments: number;
}

export interface DepartmentCostRow {
  departmentId: string;
  departmentName: string;
  departmentCode: string;
  budgetLimit: number;
  actualSpend: number;
  variance: number;
  variancePercent: number;
  transactionCount: number;
  topCategories: { name: string; amount: number }[];
}

export interface DepartmentMonthlyTrend {
  period: string;
  [departmentName: string]: string | number;
}

export interface DepartmentCategoryBreakdown {
  departmentName: string;
  categoryName: string;
  amount: number;
}

export const useDepartmentCostReport = (filters: DepartmentCostFilters) => {
  const { data: expenses, isLoading: expLoading } = useExpenses({
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
  });
  const { data: paymentRequests, isLoading: prLoading } = usePaymentRequests();
  const { data: departments, isLoading: deptLoading } = useDepartments();
  const { data: categories } = useExpenseCategories();

  const filteredPaymentRequests = useMemo(() => {
    if (!paymentRequests) return [];
    return paymentRequests.filter(pr => {
      if (pr.status !== 'paid') return false;
      const prDate = pr.paid_at || pr.submitted_at || pr.created_at;
      if (filters.dateFrom && prDate < filters.dateFrom) return false;
      if (filters.dateTo && prDate > filters.dateTo) return false;
      return true;
    });
  }, [paymentRequests, filters.dateFrom, filters.dateTo]);

  const departmentRows = useMemo((): DepartmentCostRow[] => {
    if (!departments) return [];
    const paidExpenses = expenses?.filter(e => e.payment_status === 'paid') || [];

    return departments.map(dept => {
      const deptExpenses = paidExpenses.filter(e => e.department_id === dept.id);
      const deptPRs = filteredPaymentRequests.filter(pr => pr.department_id === dept.id);

      const expenseTotal = deptExpenses.reduce((sum, e) => sum + (e.net_payment ?? e.total_amount), 0);
      const prTotal = deptPRs.reduce((sum, pr) => sum + (pr.expense?.[0]?.net_payment ?? pr.total_amount), 0);
      const actualSpend = expenseTotal + prTotal;

      // Category breakdown
      const catMap = new Map<string, number>();
      deptExpenses.forEach(e => {
        const catName = e.category?.name || 'Uncategorized';
        catMap.set(catName, (catMap.get(catName) || 0) + (e.net_payment ?? e.total_amount));
      });
      const topCategories = Array.from(catMap.entries())
        .map(([name, amount]) => ({ name, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 3);

      const budget = dept.budget_limit || 0;
      const variance = budget - actualSpend;
      const variancePercent = budget > 0 ? ((actualSpend / budget) * 100) : 0;

      return {
        departmentId: dept.id,
        departmentName: dept.name,
        departmentCode: dept.code,
        budgetLimit: budget,
        actualSpend,
        variance,
        variancePercent,
        transactionCount: deptExpenses.length + deptPRs.length,
        topCategories,
      };
    }).sort((a, b) => b.actualSpend - a.actualSpend);
  }, [departments, expenses, filteredPaymentRequests]);

  const kpis = useMemo((): DepartmentCostKPIs => {
    const totalSpend = departmentRows.reduce((sum, r) => sum + r.actualSpend, 0);
    const highest = departmentRows[0];
    const withBudget = departmentRows.filter(r => r.budgetLimit > 0);
    const avgUtil = withBudget.length > 0
      ? withBudget.reduce((sum, r) => sum + r.variancePercent, 0) / withBudget.length
      : 0;
    const overBudget = departmentRows.filter(r => r.budgetLimit > 0 && r.actualSpend > r.budgetLimit).length;

    return {
      totalSpend,
      highestSpendDepartment: highest?.departmentName || 'N/A',
      highestSpendAmount: highest?.actualSpend || 0,
      avgBudgetUtilization: Math.round(avgUtil * 10) / 10,
      departmentsOverBudget: overBudget,
      totalDepartments: departmentRows.length,
    };
  }, [departmentRows]);

  const monthlyTrends = useMemo((): DepartmentMonthlyTrend[] => {
    if (!departments) return [];
    const paidExpenses = expenses?.filter(e => e.payment_status === 'paid') || [];
    const periodMap = new Map<string, Record<string, number>>();

    paidExpenses.forEach(exp => {
      const period = format(parseISO(exp.expense_date), 'yyyy-MM');
      const deptName = exp.department?.name || 'Unassigned';
      if (!periodMap.has(period)) periodMap.set(period, {});
      const rec = periodMap.get(period)!;
      rec[deptName] = (rec[deptName] || 0) + (exp.net_payment ?? exp.total_amount);
    });

    filteredPaymentRequests.forEach(pr => {
      const dateStr = pr.paid_at || pr.submitted_at || pr.created_at;
      const period = format(parseISO(dateStr), 'yyyy-MM');
      const deptName = pr.department?.name || 'Unassigned';
      if (!periodMap.has(period)) periodMap.set(period, {});
      const rec = periodMap.get(period)!;
      rec[deptName] = (rec[deptName] || 0) + (pr.expense?.[0]?.net_payment ?? pr.total_amount);
    });

    return Array.from(periodMap.entries())
      .map(([period, depts]) => ({ period, ...depts }))
      .sort((a, b) => a.period.localeCompare(b.period));
  }, [expenses, filteredPaymentRequests, departments]);

  const categoryBreakdown = useMemo((): DepartmentCategoryBreakdown[] => {
    const paidExpenses = expenses?.filter(e => e.payment_status === 'paid') || [];
    const results: DepartmentCategoryBreakdown[] = [];
    const map = new Map<string, number>();

    paidExpenses.forEach(exp => {
      const deptName = exp.department?.name || 'Unassigned';
      const catName = exp.category?.name || 'Uncategorized';
      const key = `${deptName}|||${catName}`;
      map.set(key, (map.get(key) || 0) + (exp.net_payment ?? exp.total_amount));
    });

    map.forEach((amount, key) => {
      const [departmentName, categoryName] = key.split('|||');
      results.push({ departmentName, categoryName, amount });
    });

    return results.sort((a, b) => b.amount - a.amount);
  }, [expenses]);

  const departmentNames = useMemo(() => {
    return departmentRows.filter(r => r.actualSpend > 0).map(r => r.departmentName);
  }, [departmentRows]);

  return {
    isLoading: expLoading || prLoading || deptLoading,
    kpis,
    departmentRows,
    monthlyTrends,
    categoryBreakdown,
    departmentNames,
  };
};
